// ============================================================================
// richText — SAFE, XSS-proof rich-text renderer for message bodies (Wave 5+).
// ----------------------------------------------------------------------------
// renderRichText(body) TOKENIZES a PLAIN-TEXT string and returns React NODES
// (JSX) — it NEVER builds an HTML string and NEVER uses dangerouslySetInnerHTML.
// Because every literal run is emitted as a React text node, React escapes it by
// default, so a hostile body like `<script>alert(1)</script>`, `<b>`, or
// `<img onerror=...>` renders as VISIBLE TEXT and is never executed.
//
// Supported, in this precedence:
//   1. auto-linkified URLs — http://, https://, and mailto: only. The scheme is
//      validated; anything else (javascript:, data:, vbscript:, file:, …) is
//      left as plain text and never becomes an href.
//   2. **bold**   -> <strong>
//   3. _italic_   -> <em>
//   4. newlines (\n, including \r\n) -> <br/>
//
// Everything else stays a plain React text node. Unmatched / adjacent markers
// (a lone `**`, a stray `_`, or `**bold` with no closer) degrade to literal
// text — they are never silently dropped. Bold and italic may nest.
//
// Links are styled on-brand (coral + underline, big tap target, wrap long URLs)
// and always carry target="_blank" rel="noopener noreferrer nofollow". On the
// viewer's own coral bubbles (`onCoral`) the link inverts to white so it stays
// legible against the gradient.
// ============================================================================
import type { ReactNode } from "react";

/** Options for {@link renderRichText}. */
export interface RenderRichTextOptions {
  /**
   * True when rendering inside the viewer's own (coral-gradient, white-text)
   * bubble — links invert to white/underline so they stay legible. Defaults to
   * false (the neutral bubble: coral links).
   */
  onCoral?: boolean;
}

/** Schemes we will turn into a real <a href>. Everything else stays text. */
const SAFE_SCHEMES = ["http://", "https://", "mailto:"] as const;

/**
 * Matches a URL candidate: an http://, https://, or mailto: run, anchored at a
 * word boundary so the scheme can't be picked up from inside another token
 * (e.g. `foohttp://x` does not match). We intentionally do NOT linkify bare
 * `www.` hosts — only the three explicit safe schemes — so a substring like
 * `foowww.evil.com` or the tail of a `javascript://www.evil.com` is never
 * promoted to a clickable external link. Trailing punctuation is trimmed by the
 * caller after the match.
 */
const URL_RE = /\b(?:https?:\/\/|mailto:)[^\s<>]+/gi;

/** Trailing punctuation that should not be swallowed into a link. */
const TRAILING_PUNCT = /[.,;:!?)\]}'"…]+$/;

/** Whitespace / control chars that must never appear inside an emitted href. */
// eslint-disable-next-line no-control-regex
const CONTROL_CHARS = /[\x00-\x20\x7f]/;

/**
 * Validate a URL candidate into a SAFE href, or return null. Only http://,
 * https://, and mailto: survive; everything else (javascript:, data:,
 * vbscript:, file:, …) returns null and is left as plain, escaped text. The
 * scheme check is done on a lowercased copy so `JavaScript:` can't sneak past.
 */
function safeHref(candidate: string): string | null {
  // Defense in depth: a href must contain no whitespace or control chars.
  // Browsers strip tabs/newlines that appear inside a scheme (e.g.
  // "java\tscript:"). The URL regex already stops at whitespace, so this is
  // belt-and-braces, but it guards against any future change to the matcher.
  if (CONTROL_CHARS.test(candidate)) return null;
  const lower = candidate.toLowerCase();
  const ok = SAFE_SCHEMES.some((s) => lower.startsWith(s));
  if (!ok) return null;
  return candidate;
}

/** On-brand link class: coral + underline on neutral, white on coral bubbles. */
function linkClass(onCoral: boolean): string {
  const base =
    "underline underline-offset-2 decoration-2 break-words [overflow-wrap:anywhere] rounded-sm font-bold transition-colors";
  return onCoral
    ? `${base} text-white/95 decoration-white/60 hover:text-white`
    : `${base} text-coral-600 decoration-coral/40 hover:text-coral`;
}

/** Match _italic_; emits literal text for unmatched underscores. */
function applyItalic(text: string, keyPrefix: string): ReactNode[] {
  const out: ReactNode[] = [];
  let rest = text;
  let i = 0;
  let cursor = 0;

  while (cursor < rest.length) {
    const open = rest.indexOf("_", cursor);
    if (open === -1) break;
    const close = rest.indexOf("_", open + 1);
    // Require non-empty content; otherwise treat the underscore literally.
    if (close === -1 || close === open + 1) {
      cursor = open + 1;
      continue;
    }
    if (open > 0) out.push(rest.slice(0, open));
    const inner = rest.slice(open + 1, close);
    out.push(
      <em key={`${keyPrefix}-i${i}`} className="italic">
        {inner}
      </em>,
    );
    rest = rest.slice(close + 1);
    cursor = 0;
    i += 1;
  }

  if (rest.length) out.push(rest);
  return out;
}

/**
 * Match **bold**; recurse into the inner text for italics, and run an italics
 * pass over the surrounding (non-bold) text too. Bold is matched first so its
 * `**` is never confused with italic's single `_`.
 */
function applyBold(text: string, keyPrefix: string): ReactNode[] {
  const out: ReactNode[] = [];
  let rest = text;
  let i = 0;
  let cursor = 0;

  while (cursor < rest.length) {
    const open = rest.indexOf("**", cursor);
    if (open === -1) break;
    const close = rest.indexOf("**", open + 2);
    // Require non-empty content between the markers; else treat `**` literally.
    if (close === -1 || close === open + 2) {
      cursor = open + 2;
      continue;
    }
    if (open > 0) {
      out.push(...applyItalic(rest.slice(0, open), `${keyPrefix}-b${i}p`));
    }
    const inner = rest.slice(open + 2, close);
    out.push(
      <strong key={`${keyPrefix}-b${i}`} className="font-bold">
        {applyItalic(inner, `${keyPrefix}-b${i}i`)}
      </strong>,
    );
    rest = rest.slice(close + 2);
    cursor = 0;
    i += 1;
  }

  if (rest.length) out.push(...applyItalic(rest, `${keyPrefix}-bt${i}`));
  return out;
}

/**
 * Format inline emphasis inside a URL-free run: **bold** then _italic_.
 */
function renderInline(text: string, keyPrefix: string): ReactNode[] {
  return applyBold(text, keyPrefix);
}

/**
 * Render a single line: linkify URLs, then format the non-URL segments.
 * Returns React nodes (strings + <a>/<strong>/<em>).
 */
function renderLine(
  line: string,
  keyPrefix: string,
  onCoral: boolean,
): ReactNode[] {
  const out: ReactNode[] = [];
  let lastIndex = 0;
  let match: RegExpExecArray | null;
  let i = 0;

  URL_RE.lastIndex = 0;
  while ((match = URL_RE.exec(line)) !== null) {
    const start = match.index;
    const whole = match[0];

    // Text before the URL — format it (bold/italic).
    if (start > lastIndex) {
      out.push(
        ...renderInline(line.slice(lastIndex, start), `${keyPrefix}-t${i}`),
      );
    }

    // Trim trailing punctuation off the URL so "see https://x.com." keeps the
    // dot as plain text. The trailing run is emitted exactly once here; we
    // advance lastIndex past the WHOLE match (link + trailing) so it is never
    // re-scanned or re-emitted on the next iteration / tail.
    const token = whole.replace(TRAILING_PUNCT, "");
    const trailing = whole.slice(token.length);

    const href = safeHref(token);
    if (href) {
      out.push(
        <a
          key={`${keyPrefix}-a${i}`}
          href={href}
          target="_blank"
          rel="noopener noreferrer nofollow"
          className={linkClass(onCoral)}
        >
          {token}
        </a>,
      );
    } else {
      // Not a safe scheme — keep the raw run as plain (escaped) text.
      out.push(token);
    }

    if (trailing) {
      out.push(...renderInline(trailing, `${keyPrefix}-tp${i}`));
    }

    lastIndex = start + whole.length;
    i += 1;
  }

  // Tail after the last URL.
  if (lastIndex < line.length) {
    out.push(...renderInline(line.slice(lastIndex), `${keyPrefix}-t${i}`));
  }

  return out;
}

/**
 * Render a plain-text message body as SAFE rich text (React nodes, never HTML).
 * Newlines become <br/>; each line is linkified + emphasis-formatted.
 */
export function renderRichText(
  body: string,
  options: RenderRichTextOptions = {},
): ReactNode {
  const onCoral = options.onCoral ?? false;
  if (!body) return null;

  // Normalize CRLF/CR -> LF, then split into lines (kept, even if empty).
  const lines = body.replace(/\r\n?/g, "\n").split("\n");

  const nodes: ReactNode[] = [];
  lines.forEach((line, idx) => {
    if (idx > 0) nodes.push(<br key={`br-${idx}`} />);
    if (line.length) nodes.push(...renderLine(line, `l${idx}`, onCoral));
  });

  return nodes;
}
